/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import type { ReactNode } from 'react';
import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import type { RootState } from '../store/store';
import { setAuthData } from '../store/slices/authSlice';
import { engageAuthorization } from '../services/loginService';
import Loader from 'src/components/Loader';

interface ProtectedRouteProps {
  children: ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { token } = useSelector((state: RootState) => state.auth);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [isAuthenticating, setIsAuthenticating] = React.useState(false);
  const authAttempted = React.useRef(false);

  useEffect(() => {
    // 1. EXTRACT URL PARAMETERS:
    // Check if the user is redirected here from another application containing authentication tokens.
    const searchParams = new URLSearchParams(location.search);
    const productAuthToken = searchParams.get('productAuthToken');
    const engageToken = searchParams.get('engageToken');

    // 2. DEFINE URL AUTHENTICATION PROCESS:
    // This function authenticates external URL tokens and logs the user in automatically.
    const performUrlAuth = async (pAuthToken: string, eToken: string): Promise<void> => {
      // Prevent duplicate API calls if this effect runs twice (e.g., React Strict Mode)
      if (authAttempted.current) return;
      authAttempted.current = true;

      setIsAuthenticating(true);
      try {
        // Authenticate the productAuthToken with the backend to fetch the user profile & secondary tokens
        const response = await engageAuthorization(pAuthToken);

        // The API returns the data directly at the top level
        // We check for response.token to confirm the authentication was successful
        if (response.token !== undefined && response.token !== '') {
          const { user, token: newToken, role, appConfigs, appRights } = response;

          // Prepare the formatted data for Redux
          const authData = {
            user,
            token: newToken,
            role,
            superadminClientId: String(user.clientId),
            appConfigs,
            appRights
          };

          // 3. STORE SESSION CONFIGURATIONS:

          // Save specific app configs (e.g., Webhook settings) to sync with main app behavior
          const webhookConfig: { configName: string;[key: string]: unknown } | undefined = Array.isArray(appConfigs)
            ? (appConfigs as { configName: string;[key: string]: unknown }[]).find(config => config.configName === "WebhookURLCount")
            : undefined;
          if (webhookConfig !== undefined) localStorage.setItem('WebhookURLCount', JSON.stringify(webhookConfig));

          // Save the raw response and tokens so the session persists upon manual reload
          sessionStorage.setItem('engageAuthorizationResponse', JSON.stringify(response));
          sessionStorage.setItem('engagetoken', eToken);

          // Save the required tokens used by the Axios Interceptor
          localStorage.setItem('jwtToken', newToken);
          localStorage.setItem('engagetoken', eToken);

          // 4. UPDATE GLOBAL STATE:
          // Update Redux state so React reactivity updates properly
          dispatch(setAuthData(authData));

          // 5. URL CLEANUP REDIRECT:
          // Navigate to the clean `/dashboard` route. 
          // `replace: true` overwrites browser history so the token-filled URL is permanently hidden.
          navigate('/dashboard', { replace: true });
        } else {
          // If the API didn't return a token, treat as a failed login and securely redirect
          navigate('/', { state: { from: location }, replace: true });
        }
      } catch (error) {
        // Fall back to the login page entirely if the API throws an error
        navigate('/', { state: { from: location }, replace: true });
      } finally {
        setIsAuthenticating(false);
      }
    };

    // Trigger URL authentication flow if the tokens exist in the route
    if (productAuthToken !== null && engageToken !== null) {
      void performUrlAuth(productAuthToken, engageToken);
      return; // Exit early to wait for `performUrlAuth` to complete and trigger the redirect
    }

    // 6. NORMAL PROTECTED ROUTE FLOW:
    // If there are no URL tokens, verify the user's existing login state.
    // Check localStorage as well to avoid race conditions after dispatch.
    const currentToken = token ?? localStorage.getItem('jwtToken');

    if (currentToken != null) {
      try {
        // Decode the existing JWT to ensure it hasn't expired
        const dJwt = jwtDecode(currentToken);
        const current_time = Date.now() / 1000;
        const exp_time = (dJwt.exp != null) ? dJwt.exp : current_time;

        if (current_time > exp_time) {
          // Kick the user back to the login page if their session timed out
          navigate('/', { state: { from: location }, replace: true });
        }
      } catch (err) {
        navigate('/', { state: { from: location }, replace: true });
      }
    } else if (!isAuthenticating && productAuthToken === null) {
      // Only navigate to login if we explicitly have NO token and we aren't currently waiting for an API response
      // `state: { from: location }` saves the user's attempted route so they can be redirected back after logging in.
      navigate('/', { state: { from: location }, replace: true });
    }
  }, [location.search, token, navigate, dispatch, isAuthenticating]);

  if (isAuthenticating) {
    return (
      <div className="text-center"><Loader /></div>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
